import Box from "@mui/material/Box";
import type {ColumnDef} from "@tanstack/react-table";
import type {AggregationBookingEngineListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import {BookingEngineAggregationService} from "crqlar-react-common/dist/services/aggregation/bookingEngineAggregation.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailFullscreen,
    ServerTableRailSearch,
    ServerTableRailSort,
    useServerTable,
} from "../../common/serverTable";
import {
    RightSidebarCreateButton,
    RightSidebarDivider,
    RightSidebarRail,
    RightSidebarRailLayout,
} from "../../layout/rightSidebar/RightSidebarRail";
import {DateElement} from "../../common/uiElements/DateElement";

const BookingEngineConfigsList = () => {
    useTrackScreenVisit("booking engines");

    const navigate = useNavigate();
    const {t} = useTranslation();

    const columns = useMemo<ColumnDef<AggregationBookingEngineListItem>[]>(() => [
        {
            id: "name",
            header: t("Name"),
            accessorFn: bookingEngine => bookingEngine.name,
            cell: ({row}) => <Box>{row.original.name ?? ""}</Box>,
            size: 320,
            meta: {defaultActive: true, isServerSortable: true, grow: true},
        },
        {
            id: "sector",
            header: t("Sector"),
            accessorFn: bookingEngine => bookingEngine.sector?.name,
            cell: ({row}) => <Box>{row.original.sector?.name ?? ""}</Box>,
            size: 220,
            meta: {defaultActive: true},
        },
        {
            id: "serviceCount",
            header: t("Services"),
            accessorFn: bookingEngine => bookingEngine.serviceCount,
            cell: ({row}) => (
                <Box sx={{fontVariantNumeric: "tabular-nums"}}>
                    {row.original.serviceCount}
                </Box>
            ),
            size: 140,
            meta: {defaultActive: true},
        },
        {
            id: "insertDate",
            header: t("Created"),
            accessorFn: bookingEngine => bookingEngine.insertDate,
            cell: ({row}) => <DateElement date={row.original.insertDate}/>,
            size: 200,
            meta: {defaultActive: false, isServerSortable: true},
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: bookingEngine => bookingEngine.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true},
        },
        {
            id: "updateDate",
            header: t("Last updated"),
            accessorFn: bookingEngine => bookingEngine.updateDate,
            cell: ({row}) => <DateElement date={row.original.updateDate}/>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true},
        },
    ], [t]);

    const controller = useServerTable<AggregationBookingEngineListItem>({
        tableId: "booking-engine-configurations",
        columns,
        getRowId: bookingEngine => bookingEngine.id,
        onRowClick: bookingEngine => navigate(`/bookingEngine/configurations/${bookingEngine.id}`),
        initialSorting: [{id: "name", desc: false}],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await BookingEngineAggregationService.listBookingEngines({
                name: searchQuery ? [searchQuery] : undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content,
                totalElements: page.totalElements,
            };
        },
    });

    return (
        <Box sx={{display: "flex", flex: 1, minWidth: 0, minHeight: 0}}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <RightSidebarRailLayout>
                        <ServerTable/>

                        <RightSidebarRail>
                            <ServerTableRailSearch/>
                            <ServerTableRailSort/>
                            <ServerTableRailFullscreen/>
                            <ServerTableRailColumns/>
                            <RightSidebarDivider/>
                            <RightSidebarCreateButton
                                label={t("Create new")}
                                onClick={() => navigate("/bookingEngine/configurations/new")}
                            />
                        </RightSidebarRail>
                    </RightSidebarRailLayout>
                </ServerTableFrame>
            </ServerTableProvider>
        </Box>
    );
};

export default BookingEngineConfigsList;
